import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Grid,
  IconButton,
  Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { palette } from '../../theme/colors';

interface QuoteItem {
  id: string;
  productId: string;
  productName: string;
  productSku?: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  discount: number;
  notes?: string;
}

interface PlannedTransfer {
  productId: string;
  productName: string;
  quantity: number;
  fromLocationId: string;
  fromLocationName: string;
  toLocationId: string;
  toLocationName: string;
}

interface DeliveryAddress {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  notes?: string;
}

interface Quote {
  id: string;
  quoteNumber: string;
  status: string;
  validUntil: string;
  createdAt: string;
  updatedAt: string;
  items: QuoteItem[];
  subtotal: number;
  tax: number;
  discount: number;
  total: number;
  terms?: string;
  notes?: string;
  customer?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  salesRep?: {
    firstName: string;
    lastName: string;
  };
  // Transfer and delivery fields
  requiresInternalTransfer?: boolean;
  plannedTransfers?: PlannedTransfer[];
  pendingTransferIds?: string[];
  deliveryAddress?: DeliveryAddress | null;
  requiresDelivery?: boolean;
}

interface PortalContext {
  isAuthenticated: boolean;
}

const fetchQuoteDetail = async (orgSlug: string, quoteId: string, token: string): Promise<Quote> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/b2b-portal/${orgSlug}/quotes/${quoteId}`,
    {
      headers: { 'Authorization': `Bearer ${token}` },
    }
  );
  if (!response.ok) {
    throw new Error('Failed to fetch quote details');
  }
  return response.json();
};

const downloadQuotePdf = async (orgSlug: string, quoteId: string, token: string): Promise<Blob> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/b2b-portal/${orgSlug}/quotes/${quoteId}/pdf`,
    {
      headers: { 'Authorization': `Bearer ${token}` },
    }
  );
  if (!response.ok) {
    throw new Error('Failed to download PDF');
  }
  return response.blob();
};

const convertToReservation = async (orgSlug: string, quoteId: string, token: string): Promise<any> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/b2b-portal/${orgSlug}/quotes/${quoteId}/convert-to-reservation`,
    {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to convert quote to reservation');
  }
  return response.json();
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'accepted':
      return palette.success;
    case 'sent':
      return palette.primary;
    case 'draft':
      return palette.textMuted;
    case 'rejected':
    case 'expired':
      return palette.error;
    default:
      return palette.warning;
  }
};

export const B2BQuoteDetailPage: React.FC = () => {
  const { orgSlug, quoteId } = useParams<{ orgSlug: string; quoteId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useOutletContext<PortalContext>();
  const token = localStorage.getItem('access_token');

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/b2b/${orgSlug}/login`);
    }
  }, [isAuthenticated, navigate, orgSlug]);

  const { data: quote, isLoading, error } = useQuery({
    queryKey: ['b2b-quote-detail', orgSlug, quoteId],
    queryFn: () => fetchQuoteDetail(orgSlug!, quoteId!, token!),
    enabled: !!orgSlug && !!quoteId && !!token,
  });

  const convertMutation = useMutation({
    mutationFn: () => convertToReservation(orgSlug!, quoteId!, token!),
    onSuccess: (reservation) => {
      setSnackbar({
        open: true,
        message: `Quote converted to reservation #${reservation.reservationNumber || reservation.id}`,
        severity: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['b2b-quote-detail'] });
      queryClient.invalidateQueries({ queryKey: ['b2b-my-quotes'] });
      queryClient.invalidateQueries({ queryKey: ['b2b-my-reservations'] });
      // Navigate to reservations after a short delay
      setTimeout(() => {
        navigate(`/b2b/${orgSlug}/reservations`);
      }, 2000);
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to convert quote to reservation',
        severity: 'error',
      });
    },
  });

  const handleDownloadPdf = async () => {
    try {
      const blob = await downloadQuotePdf(orgSlug!, quoteId!, token!);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quote-${quote?.quoteNumber || quoteId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to download PDF',
        severity: 'error',
      });
    }
  };

  const handleConvertToReservation = () => {
    convertMutation.mutate();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !quote) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/b2b/${orgSlug}/quotes`)}
          sx={{ mb: 2 }}
        >
          Back to Quotes
        </Button>
        <Alert severity="error">
          Failed to load quote details. Please try again.
        </Alert>
      </Box>
    );
  }

  const canConvertToReservation = quote.status === 'accepted';

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(`/b2b/${orgSlug}/quotes`)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              <RequestQuoteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Quote {quote.quoteNumber}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, flexWrap: 'wrap' }}>
              <Chip
                label={quote.status.toUpperCase()}
                size="small"
                sx={{
                  bgcolor: `${getStatusColor(quote.status)}20`,
                  color: getStatusColor(quote.status),
                  fontWeight: 600,
                }}
              />
              {quote.requiresInternalTransfer && (
                <Chip
                  icon={<SwapHorizIcon />}
                  label="Requires Transfer"
                  size="small"
                  sx={{
                    bgcolor: `${palette.warning}20`,
                    color: palette.warning,
                    fontWeight: 600,
                  }}
                />
              )}
              {quote.requiresDelivery && (
                <Chip
                  icon={<LocalShippingIcon />}
                  label="Delivery"
                  size="small"
                  sx={{
                    bgcolor: `${palette.primary}20`,
                    color: palette.primary,
                    fontWeight: 600,
                  }}
                />
              )}
              <Typography variant="body2" color="text.secondary">
                Created: {new Date(quote.createdAt).toLocaleDateString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Valid Until: {new Date(quote.validUntil).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPdf}
          >
            Download PDF
          </Button>
          {canConvertToReservation && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<ShoppingCartCheckoutIcon />}
              onClick={handleConvertToReservation}
              disabled={convertMutation.isPending}
            >
              {convertMutation.isPending ? 'Converting...' : 'Convert to Reservation'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Status Message for Accepted Quotes */}
      {quote.status === 'accepted' && (
        <Alert severity="success" sx={{ mb: 3 }}>
          This quote has been accepted! You can now convert it to a reservation to secure your order.
        </Alert>
      )}

      {/* Transfer Requirements Info */}
      {quote.requiresInternalTransfer && quote.plannedTransfers && quote.plannedTransfers.length > 0 && (
        <Alert
          severity="info"
          icon={<SwapHorizIcon />}
          sx={{ mb: 3 }}
        >
          <Typography fontWeight={600} gutterBottom>
            Internal Stock Transfer Required
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Some items need to be transferred from other locations. The transfer process will begin once the quote is converted to a reservation.
          </Typography>
          <Box sx={{ mt: 1 }}>
            {quote.plannedTransfers.map((transfer, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="body2">
                  <strong>{transfer.quantity}x</strong> {transfer.productName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  from <strong>{transfer.fromLocationName}</strong> → <strong>{transfer.toLocationName}</strong>
                </Typography>
              </Box>
            ))}
          </Box>
        </Alert>
      )}

      {/* Delivery Address Info */}
      {quote.requiresDelivery && quote.deliveryAddress && (
        <Card sx={{ mb: 3, bgcolor: `${palette.primary}08`, border: `1px solid ${palette.primary}30` }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LocalShippingIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Delivery Information
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <LocationOnIcon sx={{ color: 'text.secondary', mt: 0.3 }} fontSize="small" />
              <Box>
                <Typography variant="body1">{quote.deliveryAddress.street}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {quote.deliveryAddress.city}, {quote.deliveryAddress.state} {quote.deliveryAddress.postalCode}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {quote.deliveryAddress.country}
                </Typography>
                {quote.deliveryAddress.notes && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Note: {quote.deliveryAddress.notes}
                  </Typography>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Quote Details Grid */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          {/* Items Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Quote Items
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: palette.sky }}>
                      <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Qty</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Discount</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Subtotal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {quote.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Typography fontWeight={500}>{item.productName}</Typography>
                          {item.productSku && (
                            <Typography variant="caption" color="text.secondary">
                              SKU: {item.productSku}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="right">{item.unitPrice.toFixed(2)} DZD</TableCell>
                        <TableCell align="right">
                          {item.discount > 0 ? `${item.discount.toFixed(2)} DZD` : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={600}>
                            {item.subtotal.toFixed(2)} DZD
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          {/* Summary */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Summary
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">Subtotal</Typography>
                <Typography>{quote.subtotal.toFixed(2)} DZD</Typography>
              </Box>
              {quote.discount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography color="text.secondary">Discount</Typography>
                  <Typography color="success.main">-{quote.discount.toFixed(2)} DZD</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">Tax</Typography>
                <Typography>{quote.tax.toFixed(2)} DZD</Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" fontWeight={600}>Total</Typography>
                <Typography variant="h6" fontWeight={700} color="primary">
                  {quote.total.toFixed(2)} DZD
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Terms & Notes */}
          {(quote.terms || quote.notes) && (
            <Card>
              <CardContent>
                {quote.terms && (
                  <Box sx={{ mb: quote.notes ? 2 : 0 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Terms & Conditions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {quote.terms}
                    </Typography>
                  </Box>
                )}
                {quote.notes && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Notes
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {quote.notes}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
